import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getReferral, referralAction } from '../../../Api/apiUtils';
import ClickToCall from '../../../Telephony/ClickToCall';
import moment from 'moment';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Page,
  PageHeader,
  Card,
  Button,
  Field,
  SearchInput,
  Select,
  Toolbar,
  StatusBadge,
  Table,
  Loader,
  EmptyState,
  useConfirm,
} from '../../../ui';
import './referral.scss';


interface ReferralRecord {
  timestamp: string;
  userId: string;
  status: string;
  name?: string;
  firstName?: string;
  mobileNumber?: string;
  upiId?: string;
  points?: string | number;
  // Add other properties you use
}
const Referral = () => {
  const [referralData, setReferralData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [allData, setAllData] = useState<any[]>([]); // Store all fetched data
  const [displayData, setDisplayData] = useState<any[]>([]); // Data to display
  const [page, setPage] = useState(1);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const itemsPerPage = 50; // Number of items to load each scroll

  const [pageSize, setPageSize] = useState(10);
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('');
  const [lastKey, setLastKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { confirm, dialog } = useConfirm();

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: '0', label: 'Pending' },
    { value: '1', label: 'Approved' },
    { value: '2', label: 'Rejected' }
  ];
  const sortOptions = [
    { value: 'desc', label: 'Newest first' },
    { value: 'asc', label: 'Oldest first' },
  ];
  const filteredData = useMemo(() => {
    if (!searchTerm) return allData;
    
    const term = searchTerm.toLowerCase();
    return allData.filter(item => 
      (item.name?.toLowerCase().includes(term) ||
      item.firstName?.toLowerCase().includes(term) ||
      item.mobileNumber?.includes(term) ||
      item.upiId?.toLowerCase().includes(term))
    );
  }, [allData, searchTerm]);
  useEffect(() => {
    setDisplayData(filteredData.slice(0, itemsPerPage));
    setPage(1);
    setHasMore(filteredData.length > itemsPerPage);
  }, [filteredData]);
  const fetchReferrals = async () => {
    try {
      setLoading(true);
      const response = await getReferral(
        1000, // Fetch 1000 records at once
        sortOrder,
        statusFilter === '' ? undefined : statusFilter,
        undefined,
        searchTerm || undefined
      );
      
      const items = response.data?.Items || [];
      if (Array.isArray(items)) {
        setAllData(items);
        setDisplayData(items.slice(0, itemsPerPage));
        setHasMore(items.length > itemsPerPage);
      } else {
        setAllData([]);
        setDisplayData([]);
        toast.error('Failed to fetch referrals');
      }
    } catch (error) {
      console.error("Error fetching referral data:", error);
      toast.error('An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  };

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!tableContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight * 1.1;
    
    if (isNearBottom && !loading && hasMore) {
      const nextPage = page + 1;
      const nextData = filteredData.slice(0, nextPage * itemsPerPage);
      setDisplayData(nextData);
      setPage(nextPage);
      setHasMore(nextData.length < filteredData.length);
    }
  }, [loading, hasMore, page, filteredData]);

  useEffect(() => {
    fetchReferrals();
  }, [pageSize, sortOrder, statusFilter, searchTerm]);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener('scroll', handleScroll);
      return () => tableContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const runAction = async (record: ReferralRecord, action: 'approve' | 'reject') => {
    const ok = await confirm({
      title: action === 'approve' ? 'Approve referral?' : 'Reject referral?',
      message: `Are you sure you want to ${action} this referral?`,
      confirmLabel: action === 'approve' ? 'Approve' : 'Reject',
      tone: action === 'approve' ? 'primary' : 'danger',
    });
    if (!ok) return;
    try {
      const response = await referralAction({
        timestamp: record?.timestamp,
        userId: record?.userId,
        isApprove: action === 'approve'
      });

      if (response) {
        toast.success(`Referral ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
        fetchReferrals();
      } else {
        toast.error('Action failed');
      }
    } catch (error) {
      console.error("Error performing action:", error);
      toast.error('An error occurred while performing the action');
    }
  };

  // Backend status codes: 0 pending, 1 approved, 2 rejected.
  const STATUS_BY_CODE: Record<string, string> = { '0': 'pending', '1': 'approved', '2': 'rejected' };
  const getStatusBadge = (status: string | number) => <StatusBadge status={STATUS_BY_CODE[String(status)] ?? 'unknown'} />;

  const columns = [
    {
      key: 'name',
      title: 'Name',
      render: (record:any) => record.name || record.firstName || '-'
    },
    {
      key: 'contact',
      title: 'Contact',
      render: (record:any) => record.mobileNumber
        ? <ClickToCall number={record.mobileNumber} name={record.name || record.firstName} sourceRecordId={record.userId} />
        : (record.upiId || '-')
    },
    {
      key: 'points',
      title: 'Points',
      className: 'ds-num',
      render: (record:any) => record.points || '0'
    },
    {
      key: 'userId',
      title: 'User ID',
      render: (record:any) => record.userId || '-'
    },
    {
      key: 'timestamp',
      title: 'Date',
      render: (record:any) => moment(record.timestamp).format('DD MMM YYYY, hh:mm A')
    },
    {
      key: 'status',
      title: 'Status',
      render: (record:any) => getStatusBadge(record.status)
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (record:any) => (
        String(record.status) === '0' ? (
          <div className="referral-actions">
            <Button variant="primary" size="sm" onClick={() => runAction(record, 'approve')}>
              Approve
            </Button>
            <Button variant="secondary" size="sm" onClick={() => runAction(record, 'reject')}>
              Reject
            </Button>
          </div>
        ) : null
      )
    }
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="Consumer Xperience"
        title="Referral Management"
        subtitle={loading ? 'Loading referrals…' : `${filteredData.length} referrals`}
      />

      <Card>
        <Toolbar>
          <Field label="Search" className="ds-toolbar__grow">
            {(ctl) => (
              <SearchInput
                {...ctl}
                placeholder="Name, contact or UPI ID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}
          </Field>
          <Field label="Status">
            {(ctl) => (
              <Select {...ctl} options={statusOptions} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} />
            )}
          </Field>
          <Field label="Sort by date">
            {(ctl) => (
              <Select {...ctl} options={sortOptions} value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
            )}
          </Field>
        </Toolbar>
      </Card>

      {loading ? (
        <Loader label="Loading referrals…" />
      ) : displayData.length === 0 ? (
        <EmptyState
          title="No referrals found"
          description="Try a different search, or set Status to All Status."
        />
      ) : (
        <Table caption="Referrals" stickyHeader wrapRef={tableContainerRef} wrapClassName="referral-scroll">
          <thead>
            <tr>
              {columns.map(column => (
                <th key={column.key} className={column.className}>{column.title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.map((record, index) => (
              <tr key={index}>
                {columns.map(column => (
                  <td key={`${index}-${column.key}`} className={column.className}>
                    {column.render(record)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {dialog}
    </Page>
  );
};

export default Referral;